/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { CHECKLIST_STEP_STATUS_COMPLETED, normalizeActiveChecklist } from '@/lib/threadChecklist.js';
import getPendingInteractionsFromActivity from '@/lib/threadInteractions/getPendingInteractionsFromActivity.js';
import resolveThreadActiveChecklist from '@/redux/selectors/lib/resolveThreadActiveChecklist.js';
import resolveVisibleThreadActivity from '@/redux/selectors/lib/resolveVisibleThreadActivity.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@/redux/selectors/pieveSelectors/constants.js';
import { threadContentMap, threadMetaById } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import getNormalizedThreadId from '@/redux/selectors/pieveSelectors/lib/getNormalizedThreadId.js';
import getThreadContentValue from '@/redux/selectors/pieveSelectors/lib/getThreadContentValue.js';
import getThreadMetaValue from '@/redux/selectors/pieveSelectors/lib/getThreadMetaValue.js';
import getThreadType from '@/redux/selectors/pieveSelectors/lib/getThreadType.js';
import normalizeResolvedComposerSurfaceMode from '@/redux/selectors/pieveSelectors/lib/normalizeResolvedComposerSurfaceMode.js';
import { activeThreadId } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';
import { threadContent, threadMeta } from '@/redux/selectors/pieveSelectors/threadCoreSelectors.js';
import { isAutomationsWorkspaceRoute, isThreadScreenView } from '@/redux/selectors/pieveSelectors/routeSelectors.js';
import { activeChecklistByThreadId } from '@/redux/selectors/uiSelectors.js';

const AUTOMATION_CHECKLIST_SURFACE_MODES = new Set(['schedule', 'trigger']);
const EMPTY_THREAD_CHECKLIST_STATE = Object.freeze({
  threadId: null,
  surfaceMode: '',
  checklist: EMPTY_ARRAY,
  checklistSignature: '',
  completedChecklistStepCount: 0,
  allChecklistStepsCompleted: false,
  hasVisibleChecklist: false
});
const EMPTY_ACTIVE_THREAD_CHECKLIST_STATE = Object.freeze({
  ...EMPTY_THREAD_CHECKLIST_STATE,
  canRender: false
});

export const threadActivityItemCount = (state, threadId) => {
  const activity = threadContent(state, threadId)?.activity;

  if (!Array.isArray(activity)) {
    return 0;
  }

  return activity.length;
};

export const threadActiveChecklist = (state, threadId) => {
  const normalizedThreadId = getNormalizedThreadId(threadId);

  if (!normalizedThreadId) {
    return null;
  }

  const resolvedThreadContent = threadContent(state, normalizedThreadId) || EMPTY_OBJECT;
  const resolvedThreadMeta = threadMeta(state, normalizedThreadId) || EMPTY_OBJECT;

  return resolveThreadActiveChecklist({
    activity: Array.isArray(resolvedThreadContent?.activity) ? resolvedThreadContent.activity : EMPTY_ARRAY,
    threadMeta: resolvedThreadMeta,
    runState: resolvedThreadContent?.runState?.state || resolvedThreadMeta?.runState?.state || null,
    activeRunId: resolvedThreadContent?.runState?.runId || resolvedThreadMeta?.runState?.runId || null
  });
};

export const threadChecklistState = createSelector([threadActiveChecklist, activeChecklistByThreadId, threadMetaById, (_, threadId) => getNormalizedThreadId(threadId)], (hydratedChecklist, activeChecklistByThreadIdValue, threadMetaByIdValue, normalizedThreadId) => {
  if (!normalizedThreadId) {
    return EMPTY_THREAD_CHECKLIST_STATE;
  }

  const threadMetaValue = getThreadMetaValue({
    threadMetaByIdValue,
    threadId: normalizedThreadId
  });
  const surfaceMode = normalizeResolvedComposerSurfaceMode(getThreadType(threadMetaValue));
  const checklistOverride = activeChecklistByThreadIdValue?.[normalizedThreadId];
  const resolvedChecklist = checklistOverride === undefined ? hydratedChecklist : normalizeActiveChecklist(checklistOverride);
  const checklist = Array.isArray(resolvedChecklist?.checklist) ? resolvedChecklist.checklist : EMPTY_ARRAY;
  const completedChecklistStepCount = checklist.filter((step) => step?.status === CHECKLIST_STEP_STATUS_COMPLETED).length;
  const hasChecklist = checklist.length > 0;
  const allChecklistStepsCompleted = hasChecklist && completedChecklistStepCount === checklist.length;
  const checklistSignature = checklist
    .map((step) => {
      return `${String(step?.step || '').trim()}::${String(step?.status || '').trim()}`;
    })
    .join('||');

  return {
    threadId: normalizedThreadId,
    surfaceMode,
    checklist,
    checklistSignature,
    completedChecklistStepCount,
    allChecklistStepsCompleted,
    hasVisibleChecklist: hasChecklist && !allChecklistStepsCompleted
  };
});

export const activeThreadChecklistState = createSelector([activeThreadId, isThreadScreenView, isAutomationsWorkspaceRoute, (state) => threadChecklistState(state, activeThreadId(state))], (activeThreadIdValue, isThreadScreenViewValue, isAutomationsWorkspaceRouteValue, checklistState) => {
  const normalizedThreadId = getNormalizedThreadId(activeThreadIdValue);

  if (!normalizedThreadId) {
    return EMPTY_ACTIVE_THREAD_CHECKLIST_STATE;
  }

  return {
    ...checklistState,
    canRender: Boolean(normalizedThreadId && (isThreadScreenViewValue || (isAutomationsWorkspaceRouteValue && AUTOMATION_CHECKLIST_SURFACE_MODES.has(checklistState.surfaceMode))))
  };
});

export const threadPendingInteractions = createSelector([threadContentMap, threadMetaById, (_, threadId) => getNormalizedThreadId(threadId)], (threadContentById, threadMetaByIdValue, normalizedThreadId) => {
  if (!normalizedThreadId) {
    return EMPTY_ARRAY;
  }

  const threadContentValue =
    getThreadContentValue({
      threadContentMapValue: threadContentById,
      threadId: normalizedThreadId
    }) || EMPTY_OBJECT;
  const threadMetaValue =
    getThreadMetaValue({
      threadMetaByIdValue,
      threadId: normalizedThreadId
    }) || EMPTY_OBJECT;
  const runState = threadContentValue?.runState || threadMetaValue?.runState || EMPTY_OBJECT;

  return getPendingInteractionsFromActivity({
    activity: threadContentValue?.activity,
    runState: runState?.state || 'idle',
    activeRunId: runState?.runId || null
  });
});

export const hasPendingInteractions = (state, threadId) => {
  const interactions = threadPendingInteractions(state, threadId);
  return Array.isArray(interactions) && interactions.some((i) => Boolean(String(i?.interactionId || i?.id || i?.eventId || '').trim()));
};

export const threadVisibleActivity = createSelector([threadContentMap, (_, threadId) => getNormalizedThreadId(threadId)], (threadContentById, normalizedThreadId) => {
  if (!normalizedThreadId) {
    return EMPTY_ARRAY;
  }

  const threadContentValue =
    getThreadContentValue({
      threadContentMapValue: threadContentById,
      threadId: normalizedThreadId
    }) || EMPTY_OBJECT;
  const activity = Array.isArray(threadContentValue?.activity) ? threadContentValue.activity : EMPTY_ARRAY;

  return resolveVisibleThreadActivity({
    activity
  });
});
